import { getHeroBackgroundStyle } from '../../../utils/getHeroBackgroundStyle';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useHeroManager } from '../../../hooks/useHeroManager';
import CountdownTimer from '../../../components/feature/CountdownTimer';
import { trackCTAClick } from '../../../utils/leadTracking';
import { DEFAULT_HERO_VISUAL_CONFIG } from '../../../types/hero';

/**
 * Hero da homepage conectada aos campos visuais do admin.
 * Mantém a lógica atual de scroll, tracking e conteúdo, mas troca os estilos fixos
 * por estilos dinâmicos vindos do Supabase.
 */
export default function Hero() {
  const [loaded, setLoaded] = useState(false);
  const { getActiveHero, loading } = useHeroManager();
  const activeHero = getActiveHero();

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const base = 'transition-all duration-700 ease-out';
  const visible = 'opacity-100 translate-y-0';
  const hidden = 'opacity-0 translate-y-8';

  /**
   * Projeto atual usa buttons[]; aqui identificamos o primário e secundário
   * sem alterar a estrutura original dos dados.
   */
  const primaryButton =
    activeHero?.buttons?.find((button) => button.type === 'primary') ||
    activeHero?.buttons?.[0];

  const secondaryButton =
    activeHero?.buttons?.find((button) => button.type === 'secondary') ||
    activeHero?.buttons?.[1];

  /**
   * Normalização de estilos dinâmicos para evitar strings inválidas nos campos numéricos.
   * Correção principal: a cor do overlay agora tem prioridade sobre o gradiente.
   */
  const heroStyles = useMemo(() => {
  if (!activeHero) return null;

  const parseNumber = (value: unknown, fallback: number) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const parsed = Number(String(value ?? '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const statsOpacity = parseNumber(
    activeHero.stats_block_opacity,
    DEFAULT_HERO_VISUAL_CONFIG.stats_block_opacity,
  );

  const statsBlur = parseNumber(
    activeHero.stats_block_blur,
    DEFAULT_HERO_VISUAL_CONFIG.stats_block_blur,
  );

  return {
    overlay: getHeroBackgroundStyle(activeHero),

    badge: {
      background:
        activeHero.badge_background_color ||
        DEFAULT_HERO_VISUAL_CONFIG.badge_background_color,
      color:
        activeHero.badge_text_color ||
        DEFAULT_HERO_VISUAL_CONFIG.badge_text_color,
      border: `1px solid ${
        activeHero.badge_border_color ||
        DEFAULT_HERO_VISUAL_CONFIG.badge_border_color
      }`,
      boxShadow: activeHero.badge_shadow
        ? '0 6px 18px rgba(0,0,0,0.18)'
        : 'none',
      backdropFilter: 'blur(8px)',
    } satisfies CSSProperties,

    primaryButton: {
      background:
        activeHero.button_primary_gradient ||
        activeHero.button_primary_color ||
        DEFAULT_HERO_VISUAL_CONFIG.button_primary_color,
      color:
        activeHero.button_primary_text_color ||
        DEFAULT_HERO_VISUAL_CONFIG.button_primary_text_color,
      border: `1px solid ${
        activeHero.button_primary_border_color ||
        DEFAULT_HERO_VISUAL_CONFIG.button_primary_border_color
      }`,
    } satisfies CSSProperties,

    secondaryButton: {
      background:
        activeHero.button_secondary_gradient ||
        activeHero.button_secondary_color ||
        DEFAULT_HERO_VISUAL_CONFIG.button_secondary_color,
      color:
        activeHero.button_secondary_text_color ||
        DEFAULT_HERO_VISUAL_CONFIG.button_secondary_text_color,
      border: `1px solid ${
        activeHero.button_secondary_border_color ||
        DEFAULT_HERO_VISUAL_CONFIG.button_secondary_border_color
      }`,
    } satisfies CSSProperties,

    statsCard: {
      background:
        activeHero.stats_block_background ||
        DEFAULT_HERO_VISUAL_CONFIG.stats_block_background,
      border: `1px solid ${
        activeHero.stats_block_border ||
        DEFAULT_HERO_VISUAL_CONFIG.stats_block_border
      }`,
      backdropFilter: `blur(${statsBlur}px)`,
      WebkitBackdropFilter: `blur(${statsBlur}px)`,
      opacity: statsOpacity,
      color:
        activeHero.stats_block_text_color ||
        DEFAULT_HERO_VISUAL_CONFIG.stats_block_text_color,
      boxShadow: activeHero.stats_block_shadow
        ? '0 10px 30px rgba(0,0,0,0.20)'
        : 'none',
    } satisfies CSSProperties,

    statsIcon: {
      color:
        activeHero.stats_block_icon_color ||
        DEFAULT_HERO_VISUAL_CONFIG.stats_block_icon_color,
    } satisfies CSSProperties,

    statsValue: {
      color:
        activeHero.stats_block_text_color ||
        DEFAULT_HERO_VISUAL_CONFIG.stats_block_text_color,
    } satisfies CSSProperties,

    statsLabel: {
      color:
        activeHero.stats_block_text_color ||
        DEFAULT_HERO_VISUAL_CONFIG.stats_block_text_color,
    } satisfies CSSProperties,
  };
}, [activeHero]);
  /** Scroll para contato, preservando comportamento atual. */
  const handleAnalysisClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const contactSection = document.getElementById('contato');
    if (contactSection) {
      const offset = 100;
      const elementPosition = contactSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  /** Scroll para serviços, preservando comportamento atual. */
  const handleServicesClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const servicesSection = document.getElementById('servicos');
    if (servicesSection) {
      const offset = 100;
      const elementPosition = servicesSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const handleScrollDown = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const sobreSection = document.getElementById('sobre');
    if (sobreSection) {
      const offset = 100;
      const elementPosition = sobreSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  /** Hover dinâmico vindo do admin. */
  const handlePrimaryMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!activeHero) return;
    e.currentTarget.style.background =
      activeHero.button_primary_hover ||
      activeHero.button_primary_gradient ||
      activeHero.button_primary_color ||
      DEFAULT_HERO_VISUAL_CONFIG.button_primary_hover;
  };

  const handlePrimaryMouseLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!activeHero) return;
    e.currentTarget.style.background =
      activeHero.button_primary_gradient ||
      activeHero.button_primary_color ||
      DEFAULT_HERO_VISUAL_CONFIG.button_primary_color;
  };

  const handleSecondaryMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!activeHero) return;
    e.currentTarget.style.background =
      activeHero.button_secondary_hover ||
      activeHero.button_secondary_gradient ||
      activeHero.button_secondary_color ||
      DEFAULT_HERO_VISUAL_CONFIG.button_secondary_hover;
  };

  const handleSecondaryMouseLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!activeHero) return;
    e.currentTarget.style.background =
      activeHero.button_secondary_gradient ||
      activeHero.button_secondary_color ||
      DEFAULT_HERO_VISUAL_CONFIG.button_secondary_color;
  };

  if (loading) {
    return (
      <section
        className="relative w-full min-h-screen flex items-center justify-center overflow-hidden pt-20 sm:pt-24 lg:pt-20 bg-petrol-900"
        aria-label="Carregando conteúdo principal"
      >
        <div className="text-white text-xl" role="status" aria-live="polite">
          Carregando...
        </div>
      </section>
    );
  }

  if (!activeHero || !heroStyles) {
    return (
      <section
        className="relative w-full min-h-screen flex items-center justify-center overflow-hidden pt-20 sm:pt-24 lg:pt-20 bg-petrol-900"
        aria-label="Conteúdo principal"
      >
        <div className="text-white text-xl">Nenhuma HERO ativa encontrada.</div>
      </section>
    );
  }

  return (
    <section
      className="relative w-full min-h-[100dvh] flex items-start sm:items-center justify-center overflow-hidden pt-16 sm:pt-20 lg:pt-20 pb-8 sm:pb-12 lg:pb-20"
      aria-label="Seção principal"
      style={{ contain: 'layout' }}
    >
      <div className="absolute inset-0 z-0" style={{ width: '100%', height: '100%' }}>
        <img
          src={activeHero.backgroundImage}
          alt=""
          className="w-full h-full object-cover object-center"
          fetchPriority="high"
          decoding="async"
          role="presentation"
          width="1920"
          height="1080"
          style={{ display: 'block', width: '100%', height: '100%' }}
        />
        <div className="absolute inset-0" style={heroStyles.overlay} aria-hidden="true" />
      </div>

      <div className="relative z-10 w-full overflow-hidden px-4 sm:px-6 lg:px-8 py-4 sm:py-8 lg:py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div
            className={`${base} ${loaded ? visible : hidden} inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-full mb-3 sm:mb-6`}
            style={{ transitionDelay: '0ms', ...heroStyles.badge }}
          >
            <i className="ri-shield-check-line text-xs sm:text-base" aria-hidden="true" />
            <span className="font-bold text-xs sm:text-sm tracking-wide">
              {activeHero.badge}
            </span>
          </div>

          <h1
            className={`${base} ${loaded ? visible : hidden} text-2xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-3 sm:mb-6 leading-tight tracking-tight px-2`}
            style={{
              fontFamily: "'Playfair Display', serif",
              transitionDelay: '150ms',
              wordBreak: 'break-word',
            }}
            dangerouslySetInnerHTML={{ __html: activeHero.title }}
          />

          <p
            className={`${base} ${loaded ? visible : hidden} text-sm sm:text-lg lg:text-xl text-gray-200 mb-4 sm:mb-8 leading-relaxed max-w-3xl mx-auto font-medium px-2`}
            style={{ transitionDelay: '300ms' }}
            dangerouslySetInnerHTML={{ __html: activeHero.subtitle }}
          />

          <div
            className={`${base} ${loaded ? visible : hidden} flex flex-col sm:flex-row gap-2.5 sm:gap-4 justify-center items-stretch sm:items-center mb-4 sm:mb-8 px-2`}
            style={{ transitionDelay: '450ms' }}
          >
            <a
              href={
                primaryButton?.link ||
                'https://wa.me/5577988080505?text=Ol%C3%A1%2C%20quero%20solicitar%20uma%20an%C3%A1lise%20gratuita.'
              }
              target={primaryButton?.link?.startsWith('http') ? '_blank' : undefined}
              rel={primaryButton?.link?.startsWith('http') ? 'nofollow noreferrer' : undefined}
              onClick={(e) => {
                if (!primaryButton?.link || primaryButton.link === '#contato') {
                  handleAnalysisClick(e);
                }
                trackCTAClick('Hero - CTA Primário', 'Hero', primaryButton?.link || '');
              }}
              onMouseEnter={handlePrimaryMouseEnter}
              onMouseLeave={handlePrimaryMouseLeave}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 px-5 sm:px-8 py-3 sm:py-4 rounded-xl text-sm sm:text-lg font-bold transition-all duration-300 shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
              style={heroStyles.primaryButton}
              aria-label={primaryButton?.text || 'Solicitar análise gratuita'}
            >
              <i className="ri-whatsapp-line text-lg sm:text-2xl" aria-hidden="true" />
              {primaryButton?.text || 'Solicitar Análise Gratuita'}
            </a>

            <a
              href={secondaryButton?.link || '/servicos'}
              onClick={(e) => {
                if (!secondaryButton?.link || secondaryButton.link === '#servicos') {
                  handleServicesClick(e);
                }
              }}
              onMouseEnter={handleSecondaryMouseEnter}
              onMouseLeave={handleSecondaryMouseLeave}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 px-5 sm:px-8 py-3 sm:py-4 rounded-xl text-sm sm:text-lg font-bold transition-all duration-300 shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
              style={heroStyles.secondaryButton}
              aria-label={secondaryButton?.text || 'Conhecer nossos serviços'}
            >
              {secondaryButton?.text || 'Conhecer Serviços'}
              <i className="ri-arrow-right-line text-lg sm:text-xl" aria-hidden="true" />
            </a>
          </div>

          <div
            className={`${base} ${loaded ? visible : hidden} mb-4 sm:mb-8`}
            style={{ transitionDelay: '550ms' }}
          >
            <CountdownTimer location="hero" />
          </div>

         <div 
  className={`${base} ${loaded ? visible : hidden} grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 w-full max-w-4xl mx-auto px-2`}
  style={{ transitionDelay: '600ms' }}
  role="list"
  aria-label="Estatísticas da IBX Brasil"
>
  {activeHero.stats.map((stat, index) => (
    <div
      key={`${stat.label}-${index}`}
      className="rounded-lg sm:rounded-xl p-2 sm:p-4 transition-all"
      style={heroStyles.statsCard}
      role="listitem"
    >
      <div className="flex flex-col items-center justify-center gap-1 sm:gap-2 mb-1">
        <i
          className={`${stat.icon} text-base sm:text-xl`}
          style={heroStyles.statsIcon}
        />
        <div
          className="text-lg sm:text-2xl lg:text-4xl font-bold"
          style={{ fontFamily: "'Playfair Display', serif", ...heroStyles.statsValue }}
        >
          {stat.value}
        </div>
      </div>

      <p
        className="text-[11px] sm:text-sm font-medium text-center"
        style={heroStyles.statsLabel}
      >
        {stat.label}
      </p>
    </div>
  ))}
</div>

        </div>
      </div>

      <div className="hidden lg:flex absolute bottom-8 left-0 right-0 justify-center z-10 animate-bounce">
        <a
          href="#sobre"
          onClick={handleScrollDown}
          className="flex flex-col items-center gap-2 text-white/70 hover:text-white transition-colors cursor-pointer"
          aria-label="Rolar para baixo para ver mais conteúdo"
        >
          <span className="text-xs font-medium uppercase tracking-wider">Role para baixo</span>
          <i className="ri-arrow-down-line text-2xl" aria-hidden="true" />
        </a>
      </div>
    </section>
  );
}